import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Send, X } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api, messageErreur } from "@/lib/api";
import { useDictee } from "./useDictee";
import { CarteAction, signalerMaj } from "./CarteAction";

const Echange = ({ e, onFait, onFermer }) => (
  <div className="space-y-2" data-testid="assistant-exchange">
    <p className="text-sm text-right"><span className="inline-block at-bulle-moi text-left max-w-[85%]">{e.question}</span></p>
    <p className="text-sm break-words at-bulle-ia w-fit max-w-[85%]" data-testid="assistant-reply">{e.reponse}</p>
    {e.lien && <Link to={e.lien} onClick={onFermer} className="at-btn at-btn-primary w-fit !min-h-[44px] text-sm" data-testid="assistant-reply-link">{e.lien_label || "Ouvrir"}</Link>}
    {e.action && <CarteAction action={e.action} onFait={onFait} />}
  </div>
);

export const PanneauAssistant = () => {
  const [ouvert, setOuvert] = useState(false);
  const [fiche, setFiche] = useState(null);
  const [texte, setTexte] = useState("");
  const [echanges, setEchanges] = useState([]);
  const [envoi, setEnvoi] = useState(false);
  const fin = useRef(null);
  const envoyerRef = useRef(null);
  const { actif, interim, demarrer, arreter, supporte } = useDictee((t) => envoyerRef.current?.(t));

  useEffect(() => {
    const ouvrir = (ev) => { setFiche(ev.detail?.ficheId || ev.detail?.processusId ? ev.detail : null); setOuvert(true); if (ev.detail?.ecoute) setTimeout(() => demarrer(), 300); };
    window.addEventListener("assistant:ouvrir", ouvrir);
    return () => window.removeEventListener("assistant:ouvrir", ouvrir);
  }, [demarrer]);
  useEffect(() => { fin.current?.scrollIntoView({ block: "end" }); }, [echanges, interim]);

  const envoyer = async (phrase) => {
    const q = (phrase ?? texte).trim();
    if (!q) return;
    setTexte(""); setEnvoi(true);
    try {
      const { data } = await api.post("/assistant/message", { texte: q, fiche_id: fiche?.ficheId, processus_id: fiche?.processusId });
      setEchanges((l) => [...l.slice(-5), { id: Date.now(), question: q, ...data }]);
      if (data.action?.statut === "fait") signalerMaj();
    } catch (e) { toast.error(messageErreur(e)); } finally { setEnvoi(false); }
  };
  envoyerRef.current = envoyer;
  const micro = () => {
    if (actif) return arreter();
    if (!demarrer()) toast.error("Reconnaissance vocale indisponible sur ce navigateur : écrivez votre demande.");
  };
  const fermer = (o) => { if (!o) arreter(); setOuvert(o); };

  return (
    <Sheet open={ouvert} onOpenChange={fermer}>
      <SheetContent side="bottom" className="at-popover at-panneau max-h-[88vh] flex flex-col p-4 md:p-6 md:max-w-2xl md:mx-auto" data-testid="assistant-panel">
        <SheetHeader className="text-left">
          <SheetTitle>Assistant</SheetTitle>
          <SheetDescription className="text-xs at-muted">Simulation : phrases types, aucun appel ni message vocal.</SheetDescription>
        </SheetHeader>
        {fiche && (
          <p className="text-xs font-semibold mt-2 inline-flex items-center gap-2" data-testid="assistant-fiche-context">
            {fiche.ficheId ? `Fiche : ${fiche.ficheNom}` : `Processus : ${fiche.processusNom}`}
            <button type="button" onClick={() => setFiche(null)} aria-label="Retirer la fiche" className="w-11 h-11 flex items-center justify-center" data-testid="assistant-fiche-context-clear"><X size={14} /></button>
          </p>
        )}
        <div className="flex-1 overflow-y-auto space-y-4 py-3 min-h-[120px]" data-testid="assistant-conversation">
          {echanges.length === 0 && !interim && (
            <p className="text-sm at-muted">Parlez ou écrivez. Par exemple : « crée une tâche de rappel demain à 14h pour Le Goff ».</p>
          )}
          {echanges.map((e) => <Echange key={e.id} e={e} onFermer={() => setOuvert(false)} />)}
          {(actif || interim) && <p className="text-sm italic at-muted" data-testid="assistant-live-transcript">{interim || "J'écoute…"}</p>}
          <div ref={fin} />
        </div>
        <form onSubmit={(e) => { e.preventDefault(); envoyer(); }} className="flex gap-2 pt-2">
          <button type="button" onClick={micro} aria-label={actif ? "Arrêter l'écoute" : "Parler"} className="at-micro w-12 h-12 shrink-0 flex items-center justify-center" data-testid="assistant-mic-toggle" title={supporte ? "" : "Dictée indisponible"}>
            {actif ? <MicOff size={19} /> : <Mic size={19} />}
          </button>
          <input value={texte} onChange={(e) => setTexte(e.target.value)} placeholder="Écrire une demande…" className="at-input" data-testid="assistant-input" />
          <button type="submit" disabled={envoi || !texte.trim()} aria-label="Envoyer" className="at-icon-btn at-btn-primary w-12 h-12 shrink-0" data-testid="assistant-send"><Send size={17} /></button>
        </form>
      </SheetContent>
    </Sheet>
  );
};
